import React from "react";
import PropTypes from "prop-types";
import AppUtils from "@src/utils/appUtils";
import CardContainer from "@src/components/cards/CardContainer";
import VerticalLayout from "@src/components/cards/VerticalLayout";
import {
  ExcludedBadgeCircular,
  SectionedProgressBarCircular,
} from "@src/components/atoms";
import { getBehaviourColourForPosition } from "@signolteam/shared/colours";
import { COMMON_CONTROLS } from "@src/stories/utils/common-controls";

const GoalSummaryCard = (props) => {
  const {
    targetGoal,
    targetActual,
    getDynamicContent,
    handleClick,
    industry,
    preferredEventName,
    preferredEventNamePlural,
    customiseGoalReason,
    behaviour,
    isOpen,
    isCurrent,
    isLoading,
    position,
    periodName = "period",
  } = props;

  const targetGoalValid = targetGoal !== null && targetGoal > 0;
  const targetAchieved = targetGoalValid && targetActual >= targetGoal;
  const targetExceeded = targetAchieved && targetActual >= targetGoal + 1;
  let eventName =
    targetGoal > 1 ? preferredEventNamePlural : preferredEventName;

  const dynamicStyles = getBehaviourColourForPosition(position); // new styles from dynamic content
  const neutralStyles = getBehaviourColourForPosition(0);

  let textState = "";
  if (targetAchieved) {
    textState = targetExceeded ? "succeeded_extra" : "succeeded";
  } else if (!!isCurrent) {
    textState = "current";
  } else if (!!isOpen) {
    textState = "open";
  } else {
    textState = "closed_failed";
  }
  let contentText = `captain__targets__events__${textState}`;

  if (isLoading) {
    return (
      <div className="m-2 p-12 bg-white rounded-xl shadow-lg text-sm text-center text-gray-500">
        {getDynamicContent("common__components__text__loading")}
      </div>
    );
  }
  if (!targetGoalValid) {
    contentText = "captain__targets__events__invalid";
  }
  if (!!customiseGoalReason) {
    contentText = `captain__targets__events__${customiseGoalReason}`;
    eventName =
      customiseGoalReason === "one"
        ? eventName
        : industry?.toLowerCase() === "aviation"
          ? "flying"
          : "sailing";
  }
  return (
    <CardContainer
      thickBorderColor={
        !!customiseGoalReason && customiseGoalReason !== "one"
          ? "gray"
          : `${dynamicStyles.name}`
      }
      padding={0}
      shadow={true}
      onClick={() => handleClick(behaviour, targetGoal, targetActual)}
      trackingObject={{
        actionKey: "OVERVIEW_BEHAVIOUR_TARGET_SELECTION",
        behaviourType: behaviour.behaviourAcronym,
        targetGoal: targetGoal,
        targetActual: targetActual,
      }}
    >
      <VerticalLayout layoutType={"flex-top"}>
        {/* child 1 */}
        <div className="py-4">
          {!customiseGoalReason || customiseGoalReason === "one" ? (
            <SectionedProgressBarCircular
              size="small"
              targetAchieved={targetAchieved}
              bgColor={targetAchieved && dynamicStyles.codes["300"]}
              successValue={targetActual}
              empty={!targetGoalValid}
              goalValue={targetGoal}
              eventName={eventName?.toLowerCase()}
              lineColor={dynamicStyles.codes["500"]}
              // goalColor={dynamicStyles.codes["700"]}
              successColor={dynamicStyles.codes["400"]}
              parentColor={
                targetAchieved ? "transparent" : dynamicStyles.codes["200"]
              }
              colorName={dynamicStyles.name}
              behaviourAcronym={behaviour.behaviourAcronym}
            />
          ) : customiseGoalReason === "open_untargeted" ? (
            <SectionedProgressBarCircular
              size="small"
              targetAchieved={targetAchieved}
              bgColor={targetAchieved && neutralStyles.codes["300"]}
              successValue={targetActual}
              empty={!targetGoalValid}
              goalValue={targetGoal}
              eventName={eventName?.toLowerCase()}
              lineColor={neutralStyles.codes["500"]}
              // goalColor={neutralStyles.codes["700"]}
              successColor={neutralStyles.codes["400"]}
              parentColor={
                targetAchieved ? "transparent" : neutralStyles.codes["200"]
              }
              colorName={neutralStyles.name}
              behaviourAcronym={behaviour.behaviourAcronym}
            />
          ) : (
            <ExcludedBadgeCircular />
          )}
        </div>
        {/* child 2 */}
        <div className="py-4 px-7 grow" style={{ minHeight: "120px" }}>
          <div className={`text-base leading-snug mb-3 `}>
            {getDynamicContent(contentText, {
              BEHAVIOUR_NAME: behaviour.behaviourAcronym,
              TARGET: targetGoal,
              PROGRESS: targetActual,
              EVENT_NAME: eventName?.toLowerCase(),
              PERIOD_NAME: periodName?.toLowerCase(),
              COLOUR:
                !!customiseGoalReason && customiseGoalReason !== "one"
                  ? "#656565"
                  : dynamicStyles.codes["500"],
            })}
          </div>
          <div className="text-primary-500 underline">View more</div>
        </div>
      </VerticalLayout>
    </CardContainer>
  );
};

export const goalSummaryCardControls = {
  industry: COMMON_CONTROLS.industry,
  preferredEventName: {
    propType: PropTypes.string,
    description: "Event name",
    controlType: "text",
  },
  preferredEventNamePlural: {
    propType: PropTypes.string,
    description: "Event name - plural",
    controlType: "text",
  },
  periodLabel: {
    propType: PropTypes.string,
    description: "Period type",
    controlType: "text",
  },
  behaviour: {
    propType: PropTypes.shape({
      acronym: PropTypes.string.isRequired,
    }),
    description: "Behaviour object containing acronym",
    controlType: "object",
    defaultValue: { behaviourAcronym: "RETI" },
  },
  customiseGoalReason: {
    propType: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
    description: "Customised string for special goal statements",
    controlType: "select",
    options: ["one", "none", null],
  },
  getDynamicContent: {
    propType: PropTypes.func,
    description: "Function to get dynamic content",
    disable: true,
  },
  handleClick: {
    propType: PropTypes.func,
    description: "Click handler for the card",
    disable: true,
  },
  targetGoal: {
    propType: PropTypes.number,
    description: "Number of goals set for the period",
    controlType: "number",
  },
  targetActual: {
    propType: PropTypes.number,
    description: "Number of goals achieved in the period",
    controlType: "number",
  },
  isPresent: {
    propType: PropTypes.bool,
    description: "is open period",
    controlType: "boolean",
    defaultValue: true,
  },
  position: {
    propType: PropTypes.number,
    description: "Behaviour position in this team/customer",
    controlType: "number",
    min: 1,
    max: 5,
  },
};

GoalSummaryCard.propTypes = AppUtils.getPropTypeFromControls(
  goalSummaryCardControls,
);

export default GoalSummaryCard;
